// Generic typed HTTP klient pro Routera backend.
// Pouziva fetch (web/Capacitor kompatibilni) — zadne axios deps.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClientOptions {
  /** Base URL — "/api" v dev (Vite proxy) nebo absolutni "http://..." v prod */
  baseUrl: string;
  /** Optional: getter pro Bearer token (pro mobile auth) */
  getToken?: () => string | null | Promise<string | null>;
  /** Optional: staticke hlavicky (napr. X-Admin-Key pro desktop -> server). */
  defaultHeaders?: Record<string, string>;
  /** Optional: dynamicke hlavicky volane per-request (napr. X-Workspace-Id z runtime store). */
  getDynamicHeaders?: () => Record<string, string>;
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  delete(path: string): Promise<void>;
}

async function _readErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    try {
      return await res.text();
    } catch {
      return null;
    }
  }
}

function _extractDetail(body: unknown): string {
  if (body && typeof body === "object" && "detail" in body) {
    const d = (body as { detail: unknown }).detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0] && typeof d[0] === "object" && "msg" in d[0]) {
      return String((d[0] as { msg: unknown }).msg);
    }
  }
  return "Request failed";
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const { baseUrl, getToken, defaultHeaders, getDynamicHeaders } = opts;

  async function _request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      ...(defaultHeaders ?? {}),
      ...(getDynamicHeaders ? getDynamicHeaders() : {}),
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (getToken) {
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      const errBody = await _readErrorBody(res);
      throw new ApiError(res.status, _extractDetail(errBody), errBody);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }

  return {
    get: <T>(path: string) => _request<T>("GET", path),
    post: <T>(path: string, body?: unknown) => _request<T>("POST", path, body ?? {}),
    put: <T>(path: string, body?: unknown) => _request<T>("PUT", path, body ?? {}),
    delete: (path: string) => _request<void>("DELETE", path),
  };
}
